export type CookieConsentChoice =
  | "accepted"
  | "rejected"
  | "custom";

export type CookiePreferences = {
  essential: true;
  analytics: boolean;
  marketing: boolean;
};

export type StoredCookieConsent = {
  version: number;
  choice: CookieConsentChoice;
  preferences: CookiePreferences;
  updatedAt: string;
};

export const COOKIE_CONSENT_VERSION = 1;

export const COOKIE_CONSENT_STORAGE_KEY =
  "gluviacare-cookie-consent";

export const DEFAULT_COOKIE_PREFERENCES: CookiePreferences =
  {
    essential: true,
    analytics: false,
    marketing: false,
  };

export const ACCEPTED_COOKIE_PREFERENCES: CookiePreferences =
  {
    essential: true,
    analytics: true,
    marketing: true,
  };

export function saveCookieConsent(
  choice: CookieConsentChoice,
  preferences: CookiePreferences,
) {
  const consent: StoredCookieConsent = {
    version: COOKIE_CONSENT_VERSION,
    choice,
    preferences: {
      ...preferences,
      essential: true,
    },
    updatedAt: new Date().toISOString(),
  };

  window.localStorage.setItem(
    COOKIE_CONSENT_STORAGE_KEY,
    JSON.stringify(consent),
  );

  window.dispatchEvent(
    new CustomEvent("gluviacare:cookie-consent", {
      detail: consent,
    }),
  );

  return consent;
}

export function getCookieConsent():
  | StoredCookieConsent
  | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const storedConsent =
      window.localStorage.getItem(
        COOKIE_CONSENT_STORAGE_KEY,
      );

    if (!storedConsent) {
      return null;
    }

    const parsedConsent = JSON.parse(
      storedConsent,
    ) as StoredCookieConsent;

    if (
      parsedConsent.version !==
      COOKIE_CONSENT_VERSION
    ) {
      return null;
    }

    if (
      typeof parsedConsent.preferences
        ?.analytics !== "boolean" ||
      typeof parsedConsent.preferences
        ?.marketing !== "boolean"
    ) {
      return null;
    }

    return {
      ...parsedConsent,
      preferences: {
        ...parsedConsent.preferences,
        essential: true,
      },
    };
  } catch {
    return null;
  }
}

export function clearCookieConsent() {
  window.localStorage.removeItem(
    COOKIE_CONSENT_STORAGE_KEY,
  );
}